<template>
  <div class="repair-list">
    <div class="toolbar">
      <el-select v-if="isUnifiedWorkOrders" v-model="searchField" class="toolbar-select">
        <el-option v-for="item in searchFieldOptions" :key="item.value" :label="item.label" :value="item.value" />
      </el-select>
      <el-input
        v-model="searchKeyword"
        :placeholder="searchPlaceholder"
        clearable
        class="toolbar-input"
      />
      <el-select
        v-if="!hideSourceFilter"
        v-model="sourceType"
        clearable
        placeholder="来源类型"
        class="toolbar-select"
      >
        <el-option label="维修工单" value="AFTER_SALES" />
        <el-option label="新安装问题" value="NEW_INSTALL" />
        <el-option v-if="FEATURE_PRODUCTION_ORDERS" label="生产工单" value="PRODUCTION" />
      </el-select>
      <el-select v-model="statusFilter" clearable placeholder="状态" class="toolbar-select">
        <el-option
          v-for="item in statusOptions"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        />
      </el-select>
      <el-date-picker
        v-model="reportDateRange"
        type="daterange"
        value-format="YYYY-MM-DD"
        range-separator="至"
        start-placeholder="报单开始日期"
        end-placeholder="报单结束日期"
        class="toolbar-date-range"
      />
      <el-button type="primary" :loading="loading" @click="fetchTickets">刷新</el-button>
      <el-button v-if="hasExternalLocation" @click="clearExternalLocation">清除定位</el-button>
      <el-button
        v-if="userStore.isAdmin && apiMode === 'repairs'"
        @click="downloadImportTemplate"
      >
        下载导入模板
      </el-button>
      <el-upload
        v-if="userStore.isAdmin && apiMode === 'repairs'"
        :show-file-list="false"
        accept=".xlsx,.xls"
        :before-upload="beforeImportExcel"
      >
        <el-button type="warning" :loading="importingExcel">导入 Excel</el-button>
      </el-upload>
      <el-button
        v-if="userStore.isAdmin"
        type="success"
        :loading="exporting"
        @click="exportExcel"
      >
        导出 Excel
      </el-button>
    </div>

    <el-alert
      v-if="hasExternalLocation"
      class="location-alert"
      type="info"
      :closable="false"
      show-icon
      :title="locationSummary"
    />

    <el-table
      ref="repairTableRef"
      v-loading="loading"
      :data="filteredTickets"
      border
      stripe
      size="small"
      empty-text="暂无工单"
      :height="repairTableHeight"
      @row-dblclick="openDetail"
    >
      <el-table-column prop="id" label="ID" width="90" />
      <el-table-column label="来源" width="110">
        <template #default="{ row }">
          <el-tag effect="plain">{{ getSourceText(row.sourceType) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="productName" label="产品" min-width="120" />
      <el-table-column prop="customerName" label="客户/门店" min-width="140" />
      <el-table-column prop="storeAuthCode" label="授权码/地址" min-width="140" />
      <el-table-column prop="contact" label="联系人" width="110" />
      <el-table-column prop="phone" label="电话" width="140" />
      <el-table-column prop="handlingMethod" label="处理方式" min-width="130" />
      <el-table-column label="分类标签" min-width="180">
        <template #default="{ row }">
          <div class="tag-list">
            <el-tag
              v-for="tag in row.categories || []"
              :key="tag.id || tag.code"
              size="small"
              :type="tag.primary ? 'danger' : 'info'"
            >
              {{ tag.name }}
            </el-tag>
            <span v-if="!row.categories || row.categories.length === 0">—</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="110">
        <template #default="{ row }">
          <el-tag :type="getStatusType(row.status)">{{ getStatusText(row.status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="时效提醒" min-width="220">
        <template #default="{ row }">
          <div class="sla-cell">
            <el-tag size="small" :type="getSlaType(row)">{{ getSlaLabel(row) }}</el-tag>
            <div class="sla-subtext">{{ getSlaSubtext(row) }}</div>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="处理人" min-width="120">
        <template #default="{ row }">
          {{ formatPersonDisplay(row.processedBy || row.assignee) }}
        </template>
      </el-table-column>
      <el-table-column label="报单时间" width="170">
        <template #default="{ row }">
          {{ formatDate(row.reportTime || row.createdAt) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="170" fixed="right">
        <template #default="{ row }">
          <el-button type="primary" text @click="openDetail(row)">查看详情</el-button>
          <el-button v-if="userStore.isAdmin" type="danger" text @click="removeTicket(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-drawer v-model="detailVisible" :title="apiMode === 'repairs' ? '保修单详情' : '工单详情'" size="68%">
      <template v-if="detailTarget">
        <div class="detail-summary-card detail-block">
          <div class="detail-summary-main">
            <div class="detail-summary-title">
              <span>{{ detailTarget.title || detailTarget.productName || (apiMode === 'repairs' ? '保修单' : '工单') }}</span>
              <el-tag :type="getStatusType(detailTarget.status)" effect="dark">{{ getStatusText(detailTarget.status) }}</el-tag>
            </div>
            <div class="detail-summary-subtitle">
              <span>{{ detailTarget.workOrderNo || `${apiMode === 'repairs' ? '保修单' : '工单'} #${detailTarget.id}` }}</span>
              <span>{{ getSourceText(detailTarget.sourceType) }}</span>
            </div>
          </div>
          <div class="detail-summary-stats">
            <div class="summary-stat">
              <span class="summary-stat-label">处理人</span>
              <span class="summary-stat-value">{{ formatPersonDisplay(detailTarget.processedBy || detailTarget.assignee) }}</span>
            </div>
            <div class="summary-stat">
              <span class="summary-stat-label">报单时间</span>
              <span class="summary-stat-value">{{ formatDate(detailTarget.reportTime || detailTarget.createdAt) }}</span>
            </div>
            <div class="summary-stat">
              <span class="summary-stat-label">{{ detailTarget.sourceType === 'PRODUCTION' ? '责任人' : '联系人' }}</span>
              <span class="summary-stat-value">{{ detailTarget.sourceType === 'PRODUCTION' ? (detailTarget.responsiblePerson || '—') : (detailTarget.contact || '—') }}</span>
            </div>
          </div>
        </div>

        <div class="detail-info-grid detail-block">
          <div class="info-card">
            <div class="detail-title">基础信息</div>
            <el-descriptions :column="2" border>
              <el-descriptions-item :label="apiMode === 'repairs' ? '保修单ID' : '工单ID'">{{ detailTarget.id }}</el-descriptions-item>
              <el-descriptions-item v-if="detailTarget.workOrderNo" label="工单编号">{{ detailTarget.workOrderNo }}</el-descriptions-item>
              <el-descriptions-item label="客户/门店">{{ detailTarget.customerName || '—' }}</el-descriptions-item>
              <el-descriptions-item label="授权码/地址">{{ detailTarget.storeAuthCode || '—' }}</el-descriptions-item>
              <el-descriptions-item :label="detailTarget.sourceType === 'PRODUCTION' ? '责任人' : '联系人'">
                {{ detailTarget.sourceType === 'PRODUCTION' ? (detailTarget.responsiblePerson || '—') : (detailTarget.contact || '—') }}
              </el-descriptions-item>
              <el-descriptions-item label="联系电话">{{ detailTarget.phone || '—' }}</el-descriptions-item>
              <el-descriptions-item label="产品名称">{{ detailTarget.productName || '—' }}</el-descriptions-item>
              <el-descriptions-item label="产品型号">{{ detailTarget.productModel || '—' }}</el-descriptions-item>
              <el-descriptions-item label="合作连锁店">{{ detailTarget.cooperativeChainName || '—' }}</el-descriptions-item>
              <el-descriptions-item label="关联设备">{{ detailTarget.deviceArchiveName || detailTarget.deviceArchiveCode || '—' }}</el-descriptions-item>
              <el-descriptions-item label="分类标签" :span="2">
                <div class="tag-list">
                  <el-tag
                    v-for="tag in detailTarget.categories || []"
                    :key="tag.id || tag.code"
                    size="small"
                    :type="tag.primary ? 'danger' : 'info'"
                  >
                    {{ tag.name }}
                  </el-tag>
                  <span v-if="!detailTarget.categories || detailTarget.categories.length === 0">—</span>
                </div>
              </el-descriptions-item>
            </el-descriptions>
          </div>

          <div class="info-card">
            <div class="detail-title">处理信息</div>
            <el-descriptions :column="2" border>
              <el-descriptions-item label="提单人">{{ formatPersonDisplay(detailTarget.createdBy, '—') }}</el-descriptions-item>
              <el-descriptions-item label="处理人">{{ formatPersonDisplay(detailTarget.processedBy || detailTarget.assignee) }}</el-descriptions-item>
              <el-descriptions-item label="结单人">{{ formatPersonDisplay(detailTarget.closedBy, '—') }}</el-descriptions-item>
              <el-descriptions-item label="处理方式">{{ detailTarget.handlingMethod || '—' }}</el-descriptions-item>
              <el-descriptions-item label="首次跟进">{{ formatDate(detailTarget.firstFollowUpAt) }}</el-descriptions-item>
              <el-descriptions-item label="及时跟进">{{ formatBool(detailTarget.isFollowedInTime) }}</el-descriptions-item>
              <el-descriptions-item label="24小时解决">{{ formatBool(detailTarget.resolvedWithin24h) }}</el-descriptions-item>
              <el-descriptions-item label="重复维修次数">{{ detailTarget.repeatRepairCount ?? 0 }}</el-descriptions-item>
              <el-descriptions-item label="相同配件重复更换">{{ formatBool(detailTarget.hasRepeatedPartChange) }}</el-descriptions-item>
              <el-descriptions-item label="时效提醒" :span="2">{{ getSlaDetailText(detailTarget) }}</el-descriptions-item>
              <el-descriptions-item label="配件到货时间">{{ formatDate(detailTarget.partsArrivedAt) }}</el-descriptions-item>
              <el-descriptions-item label="服务内容" :span="2">{{ detailTarget.serviceContent || '—' }}</el-descriptions-item>
              <el-descriptions-item label="处理结果" :span="2">{{ detailTarget.processResult || '—' }}</el-descriptions-item>
              <el-descriptions-item label="问题描述" :span="2">{{ detailTarget.description || '—' }}</el-descriptions-item>
              <el-descriptions-item label="完工备注" :span="2">{{ detailTarget.completionRemark || '—' }}</el-descriptions-item>
              <el-descriptions-item label="内部备注" :span="2">{{ detailTarget.notes || '—' }}</el-descriptions-item>
            </el-descriptions>
          </div>
        </div>

        <div class="detail-block">
          <div class="detail-title">处理日志</div>
          <div v-if="logs.length > 0" class="log-bubble-track">
            <template v-for="(log, index) in logs" :key="log.id">
              <div class="log-bubble">
                <div class="log-bubble-time">{{ formatDate(log.createdAt) }}</div>
                <div class="log-bubble-title">
                  <el-tag size="small" :type="getLogActionTagType(log.actionType)">{{ getActionText(log.actionType) }}</el-tag>
                </div>
                <div class="log-bubble-desc">{{ formatLogActionDetail(log.actionDetail) }}</div>
                <div class="log-bubble-meta">操作人：{{ formatOperatorDisplay(log.operatorName) }}</div>
              </div>
              <div v-if="index < logs.length - 1" class="log-bubble-arrow" aria-hidden="true">-></div>
            </template>
          </div>
          <el-empty v-else description="暂无处理日志" :image-size="80" />
        </div>

        <div v-if="detailTarget.sourceType !== 'PRODUCTION'" class="detail-block">
          <div class="detail-header">
            <div class="detail-title">配件更换记录</div>
            <el-button type="primary" size="small" @click="openPartDialog">新增配件更换</el-button>
          </div>
          <el-table :data="partReplacements" border stripe size="small">
            <el-table-column prop="partName" label="配件名称" min-width="140" />
            <el-table-column prop="partBrandName" label="品牌" min-width="100" />
            <el-table-column prop="partModel" label="型号" min-width="100" />
            <el-table-column prop="versionNo" label="版本" min-width="90" />
            <el-table-column prop="samePartHistoryCount" label="历史同配件次数" width="130" />
            <el-table-column label="物流">
              <template #default="{ row }">
                <div>{{ formatDate(row.shippedAt) }} 发出</div>
                <div>{{ formatDate(row.arrivedAt) }} 到货</div>
              </template>
            </el-table-column>
            <el-table-column prop="remarks" label="备注" min-width="160" />
          </el-table>
        </div>

      </template>
    </el-drawer>

    <el-dialog v-model="partDialogVisible" title="登记配件更换" width="520px">
      <el-form label-width="110px">
        <el-form-item label="配件" required>
          <el-select v-model="partForm.partId" filterable style="width: 100%" placeholder="请选择配件">
            <el-option
              v-for="item in parts"
              :key="item.id"
              :label="`${item.standardName}${item.versionNo ? ` / ${item.versionNo}` : ''}`"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="更换数量">
          <el-input-number v-model="partForm.replacementCount" :min="1" :max="20" style="width: 100%" />
        </el-form-item>
        <el-form-item label="发出时间">
          <el-date-picker v-model="partForm.shippedAt" type="datetime" style="width: 100%" />
        </el-form-item>
        <el-form-item label="到货时间">
          <el-date-picker v-model="partForm.arrivedAt" type="datetime" style="width: 100%" />
        </el-form-item>
        <el-form-item label="安装时间">
          <el-date-picker v-model="partForm.installedAt" type="datetime" style="width: 100%" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="partForm.remarks" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="partDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="savingPart" @click="submitPartReplacement">保存</el-button>
      </template>
    </el-dialog>

  </div>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { ElMessageBox } from 'element-plus'
import { TicketsAPI } from '@/request/tickets'
import { RepairAPI } from '@/request/repair'
import { ProductionWorkOrdersAPI } from '@/request/productionWorkOrders'
import { MasterDataAPI } from '@/request/masterData'
import { AdminTechniciansAPI } from '@/request/users'
import { useUserStore } from '@/stores/user'
import { FEATURE_PRODUCTION_ORDERS } from '@/config/featureFlags'

export default {
  name: 'RepairList',
  props: {
    externalFilters: {
      type: Object,
      default: () => ({})
    },
    fixedSourceType: {
      type: String,
      default: ''
    },
    hideSourceFilter: {
      type: Boolean,
      default: false
    },
    apiMode: {
      type: String,
      default: 'tickets'
    },
    listScope: {
      type: String,
      default: 'all'
    }
  },
  setup(props) {
    const userStore = useUserStore()
    const tickets = ref([])
    const logs = ref([])
    const partReplacements = ref([])
    const parts = ref([])
    const loading = ref(false)
    const exporting = ref(false)
    const importingExcel = ref(false)
    const detailVisible = ref(false)
    const detailTarget = ref(null)
    const partDialogVisible = ref(false)
    const savingPart = ref(false)
    const searchKeyword = ref('')
    const searchField = ref('WORK_ORDER_NO')
    const searchFieldOptions = [
      { label: '全部字段', value: 'ALL' },
      { label: '工单编号', value: 'WORK_ORDER_NO' },
      { label: '工单ID', value: 'ID' },
      { label: '客户/门店', value: 'CUSTOMER_NAME' },
      { label: '产品名称', value: 'PRODUCT_NAME' },
      { label: '授权码/地址', value: 'STORE_AUTH_CODE' },
      { label: '联系人', value: 'CONTACT' },
      { label: '联系电话', value: 'PHONE' },
      { label: '内容', value: 'CONTENT' }
    ]
    const sourceType = ref('')
    const statusFilter = ref('')
    const reportStartDateFilter = ref('')
    const reportEndDateFilter = ref('')
    const reportDateRange = ref([])
    const externalLocationLabel = ref('')
    const externalLocationActive = ref(false)
    const pendingOpenTicketId = ref(null)
    const lastExternalRequestId = ref(null)
    const repairTableRef = ref(null)
    const repairTableHeight = ref(undefined)
    const countdownTimer = ref(null)
    const managedUsers = ref([])
    const nowTick = ref(Date.now())
    const partForm = ref({
      partId: null,
      replacementCount: 1,
      shippedAt: '',
      arrivedAt: '',
      installedAt: '',
      remarks: ''
    })
    const activeApi = computed(() => (props.apiMode === 'repairs' ? RepairAPI : TicketsAPI))
    const apiMode = computed(() => props.apiMode)
    const isUnifiedWorkOrders = computed(() => apiMode.value === 'work-orders')
    const isAssignedScope = computed(() => props.listScope === 'assigned')
    const normalizeRepairStatus = (status) => ({
      PENDING: 'PENDING',
      IN_PROGRESS: 'IN_PROGRESS',
      PROCESSING: 'IN_PROGRESS',
      PENDING_VERIFY: 'IN_PROGRESS',
      COMPLETED: 'COMPLETED',
      CLOSED: 'COMPLETED',
      CANCELLED: 'COMPLETED'
    }[status] || status || '')

    const statusOptions = computed(() => {
      if (isUnifiedWorkOrders.value) {
        return [
          { label: '未处理', value: 'PENDING' },
          { label: '处理中', value: 'PROCESSING' },
          { label: '已完成', value: 'COMPLETED' },
          { label: '已结单', value: 'CLOSED' },
          { label: '已取消', value: 'CANCELLED' }
        ]
      }
      return [
        { label: '未处理', value: 'PENDING' },
        { label: '处理中', value: 'IN_PROGRESS' },
        { label: '已结单', value: 'COMPLETED' }
      ]
    })

    const normalizeProductionWorkOrder = (item) => ({
      id: item.id,
      workOrderNo: item.workOrderNo,
      title: item.title,
      sourceType: 'PRODUCTION',
      customerName: item.storeIdentifier || '',
      storeAuthCode: item.storeIdentifier || '',
      productName: item.machineName || '',
      productModel: item.machineModel || '',
      responsiblePerson: item.responsiblePerson || '',
      description: item.issueContent || '',
      serviceContent: item.reportContent || '',
      status: item.status,
      reportTime: item.reportedAt || item.createdAt,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      contact: '',
      phone: '',
      handlingMethod: '',
      categories: item.categoryName
        ? [{ id: item.categoryId, name: item.categoryName, primary: true }]
        : [],
      assignee: item.processedByName ? { username: item.processedByName } : null,
      createdBy: item.createdByName ? { username: item.createdByName } : null,
      processedBy: item.processedByName ? { username: item.processedByName } : null,
      closedBy: item.closedByName ? { username: item.closedByName } : null,
      deviceArchiveId: item.deviceArchiveId || null,
      deviceArchiveCode: item.deviceCode || '',
      deviceArchiveName: item.deviceName || ''
    })

    const sortByReportedAtDesc = (list) => {
      return [...list].sort((a, b) => {
        const aTime = new Date(a.reportTime || a.createdAt || 0).getTime()
        const bTime = new Date(b.reportTime || b.createdAt || 0).getTime()
        return bTime - aTime
      })
    }

    const fetchTickets = async () => {
      try {
        loading.value = true
        if (isUnifiedWorkOrders.value) {
          const requestedSource = props.fixedSourceType || sourceType.value || ''
          if (isAssignedScope.value) {
            const assignedList = await TicketsAPI.getAssignedToMe()
            const normalized = Array.isArray(assignedList) ? assignedList : []
            tickets.value = sortByReportedAtDesc(normalized.filter((item) => {
              if (requestedSource && item.sourceType !== requestedSource) return false
              if (statusFilter.value && item.status !== statusFilter.value) return false
              const reportDate = item.reportTime || item.createdAt
              if (reportStartDateFilter.value && reportDate && String(reportDate).slice(0, 10) < reportStartDateFilter.value) return false
              if (reportEndDateFilter.value && reportDate && String(reportDate).slice(0, 10) > reportEndDateFilter.value) return false
              return true
            }))
          } else {
            const loadMaintenance = requestedSource !== 'PRODUCTION'
            const loadProduction = !requestedSource || requestedSource === 'PRODUCTION'
            const [maintenanceList, productionList] = await Promise.all([
              loadMaintenance
                ? TicketsAPI.getAll({
                  sourceType: requestedSource && requestedSource !== 'PRODUCTION' ? requestedSource : undefined,
                  status: statusFilter.value || undefined,
                  keyword: searchKeyword.value.trim() || undefined,
                  searchField: searchField.value || undefined,
                  reportStartDate: reportStartDateFilter.value || undefined,
                  reportEndDate: reportEndDateFilter.value || undefined
                })
                : Promise.resolve([]),
              loadProduction
                ? ProductionWorkOrdersAPI.list({
                  status: statusFilter.value || undefined,
                  startDate: reportStartDateFilter.value || undefined,
                  endDate: reportEndDateFilter.value || undefined
                })
                : Promise.resolve([])
            ])
            tickets.value = sortByReportedAtDesc([
              ...(Array.isArray(maintenanceList) ? maintenanceList : []),
              ...(Array.isArray(productionList) ? productionList.map(normalizeProductionWorkOrder) : [])
            ])
          }
        } else {
          const list = await activeApi.value.getRepairList
            ? await activeApi.value.getRepairList({
              sourceType: props.fixedSourceType || sourceType.value || undefined,
              keyword: searchKeyword.value.trim() || undefined,
              reportStartDate: reportStartDateFilter.value || undefined,
              reportEndDate: reportEndDateFilter.value || undefined
            })
            : await activeApi.value.getAll({
              sourceType: props.fixedSourceType || sourceType.value || undefined,
              keyword: searchKeyword.value.trim() || undefined,
              reportStartDate: reportStartDateFilter.value || undefined,
              reportEndDate: reportEndDateFilter.value || undefined
            })
          tickets.value = Array.isArray(list) ? list : []
        }
        if (pendingOpenTicketId.value) {
          const matched = tickets.value.find((item) => item.id === pendingOpenTicketId.value)
          if (matched) {
            pendingOpenTicketId.value = null
            await openDetail(matched)
          }
        }
      } catch (err) {
        showError(err?.message || (apiMode.value === 'repairs' ? '获取保修台账失败' : '获取工单失败'))
      } finally {
        loading.value = false
        computeRepairTableHeight()
      }
    }

    const searchPlaceholder = computed(() => {
      if (!isUnifiedWorkOrders.value) {
        return '搜索客户、产品、授权码/地址、联系人、服务内容'
      }
      const current = searchFieldOptions.find((item) => item.value === searchField.value)
      return current ? `按${current.label}搜索` : '请输入搜索关键词'
    })

    const getSearchText = (item) => {
      const tags = (item.categories || []).map((tag) => tag.name).join(' ')
      const contentText = [
        item.description,
        item.serviceContent,
        item.processResult,
        item.handlingMethod,
        item.summary,
        item.responsiblePerson,
        tags
      ].filter(Boolean).join(' ')
      switch (searchField.value) {
        case 'WORK_ORDER_NO':
          return item.workOrderNo || ''
        case 'ID':
          return item.id != null ? String(item.id) : ''
        case 'CUSTOMER_NAME':
          return item.customerName || ''
        case 'PRODUCT_NAME':
          return [item.productName, item.productModel].filter(Boolean).join(' ')
        case 'STORE_AUTH_CODE':
          return item.storeAuthCode || ''
        case 'CONTACT':
          return item.contact || ''
        case 'PHONE':
          return item.phone || ''
        case 'CONTENT':
          return contentText
        default:
          return [
            item.workOrderNo,
            item.id != null ? String(item.id) : '',
            item.customerName,
            item.productName,
            item.productModel,
            item.storeAuthCode,
            item.contact,
            item.phone,
            item.responsiblePerson,
            contentText
          ].filter(Boolean).join(' ')
      }
    }

    const filteredTickets = computed(() => {
      const kw = searchKeyword.value.trim().toLowerCase()
      return tickets.value.filter((item) => {
        if (!isUnifiedWorkOrders.value) {
          const normalizedStatus = normalizeRepairStatus(item.status)
          if (statusFilter.value && normalizedStatus !== statusFilter.value) {
            return false
          }
          const tags = (item.categories || []).map((tag) => tag.name).join(' ')
          const text = [
            item.customerName,
            item.productName,
            item.productModel,
            item.storeAuthCode,
            item.contact,
            item.phone,
            item.serviceContent,
            item.description,
            tags
          ].filter(Boolean).join(' ').toLowerCase()
          if (!kw) return true
          return text.includes(kw)
        }
        if (!kw) return true
        const text = getSearchText(item).toLowerCase()
        return text.includes(kw)
      })
    })

    const hasExternalLocation = computed(() => {
      return externalLocationActive.value
    })

    const locationSummary = computed(() => {
      const segments = []
      if (externalLocationLabel.value) segments.push(externalLocationLabel.value)
      if (reportStartDateFilter.value || reportEndDateFilter.value) {
        segments.push(`报单时间: ${reportStartDateFilter.value || '不限'} 至 ${reportEndDateFilter.value || '不限'}`)
      }
      return segments.join(' | ')
    })

    const openDetail = async (row) => {
      detailTarget.value = row
      detailVisible.value = true
      try {
        if (row.sourceType === 'PRODUCTION') {
          const [data, logList] = await Promise.all([
            ProductionWorkOrdersAPI.getById(row.id),
            ProductionWorkOrdersAPI.getLogs(row.id)
          ])
          detailTarget.value = normalizeProductionWorkOrder(data || row)
          logs.value = Array.isArray(logList) ? logList : []
          partReplacements.value = []
        } else {
          const [data, logList, replacementList] = await Promise.all([
            activeApi.value.getRepairDetail ? activeApi.value.getRepairDetail(row.id) : activeApi.value.getById(row.id),
            activeApi.value.getLogs(row.id),
            activeApi.value.getPartReplacements(row.id)
          ])
          detailTarget.value = data || row
          logs.value = Array.isArray(logList) ? logList : []
          partReplacements.value = Array.isArray(replacementList) ? replacementList : []
        }
      } catch (err) {
        logs.value = []
        partReplacements.value = []
        showError(err?.message || (apiMode.value === 'repairs' ? '获取保修单详情失败' : '获取工单详情失败'))
      }
    }

    const openPartDialog = async () => {
      if (detailTarget.value?.sourceType === 'PRODUCTION') {
        showError('生产工单暂不支持在此处登记配件更换')
        return
      }
      if (parts.value.length === 0) {
        try {
          const list = await MasterDataAPI.getParts()
          parts.value = Array.isArray(list) ? list : []
        } catch (err) {
          showError(err?.message || '获取配件列表失败')
          return
        }
      }
      partForm.value = {
        partId: null,
        replacementCount: 1,
        shippedAt: '',
        arrivedAt: '',
        installedAt: '',
        remarks: ''
      }
      partDialogVisible.value = true
    }

    const submitPartReplacement = async () => {
      if (!detailTarget.value?.id || !partForm.value.partId) {
        showError('请先选择配件')
        return
      }
      const duplicateExists = partReplacements.value.some((item) => item.partId === partForm.value.partId)
      let remindCheckCompleted = false
      if (duplicateExists) {
        try {
          await ElMessageBox.confirm('当前工单已更换过相同配件，是否已彻底检查设备？', '重复换件提醒', {
            confirmButtonText: '已检查，继续保存',
            cancelButtonText: '取消',
            type: 'warning'
          })
          remindCheckCompleted = true
        } catch {
          return
        }
      }

      try {
        savingPart.value = true
        const result = await activeApi.value.addPartReplacement(detailTarget.value.id, {
          ...partForm.value,
          deviceArchiveId: detailTarget.value.deviceArchiveId || null,
          remindCheckCompleted
        })
        if (result?.warningTriggered) {
          window.ElMessage?.warning(result.warningMessage || '配件重复更换，请彻底检查设备')
        } else {
          window.ElMessage?.success('配件更换记录已保存')
        }
        partDialogVisible.value = false
        await openDetail(detailTarget.value)
      } catch (err) {
        showError(err?.message || '保存配件更换记录失败')
      } finally {
        savingPart.value = false
      }
    }

    const exportExcel = async () => {
      try {
        exporting.value = true
        if (isUnifiedWorkOrders.value) {
          await TicketsAPI.exportWorkOrders({
            sourceType: props.fixedSourceType || sourceType.value || undefined,
            status: statusFilter.value || undefined,
            keyword: searchKeyword.value.trim() || undefined,
            searchField: searchField.value || undefined,
            reportStartDate: reportStartDateFilter.value || undefined,
            reportEndDate: reportEndDateFilter.value || undefined
          })
          window.ElMessage?.success('正在下载工单 Excel')
        } else {
          await activeApi.value.exportExcel()
          window.ElMessage?.success(apiMode.value === 'repairs' ? '正在下载保修台账 Excel' : '正在下载工单 Excel')
        }
      } catch (err) {
        showError(err?.message || (apiMode.value === 'repairs' ? '导出保修台账失败' : '导出工单失败'))
      } finally {
        exporting.value = false
      }
    }

    const buildImportMessage = (result) => {
      const importedCount = Number(result?.importedCount || 0)
      const failedCount = Number(result?.failedCount || 0)
      const errors = Array.isArray(result?.errors) ? result.errors.filter(Boolean) : []
      const lines = [`保修台账导入完成：成功 ${importedCount} 条，失败 ${failedCount} 条`]
      if (errors.length) {
        lines.push('失败明细：')
        lines.push(...errors)
      }
      return lines.join('\n')
    }

    const downloadImportTemplate = async () => {
      try {
        await RepairAPI.downloadImportTemplate()
        window.ElMessage?.success('保修台账导入模板已开始下载')
      } catch (err) {
        showError(err?.message || '下载保修台账导入模板失败')
      }
    }

    const beforeImportExcel = async (file) => {
      if (!file) return false
      try {
        importingExcel.value = true
        const result = await RepairAPI.importExcel(file)
        await ElMessageBox.alert(buildImportMessage(result), '导入结果', {
          type: result?.failedCount ? 'warning' : 'success'
        })
        await fetchTickets()
      } catch (err) {
        showError(err?.message || '导入保修台账失败')
      } finally {
        importingExcel.value = false
      }
      return false
    }

    const removeTicket = async (row) => {
      if (!row?.id) return
      const isProduction = row.sourceType === 'PRODUCTION'
      const targetName = row.workOrderNo || row.title || `${apiMode.value === 'repairs' ? '保修单' : '工单'} #${row.id}`
      try {
        await ElMessageBox.confirm(`确定删除“${targetName}”吗？删除后无法恢复。`, '提示', {
          type: 'warning',
          confirmButtonText: '删除',
          cancelButtonText: '取消'
        })
        if (isProduction) {
          await ProductionWorkOrdersAPI.deleteById(row.id)
        } else if (typeof activeApi.value.deleteById === 'function') {
          await activeApi.value.deleteById(row.id)
        } else {
          throw new Error('当前列表不支持删除')
        }
        if (detailTarget.value?.id === row.id) {
          detailVisible.value = false
          detailTarget.value = null
          logs.value = []
          partReplacements.value = []
        }
        window.ElMessage?.success(apiMode.value === 'repairs' ? '保修单已删除' : '工单已删除')
        await fetchTickets()
      } catch (err) {
        if (err === 'cancel' || err === 'close') return
        showError(err?.message || (apiMode.value === 'repairs' ? '删除保修单失败' : '删除工单失败'))
      }
    }

    const computeRepairTableHeight = () => {
      nextTick(() => {
        const tableEl = repairTableRef.value?.$el || repairTableRef.value
        if (!tableEl) return
        const headerRow = tableEl.querySelector('.el-table__header-wrapper thead tr')
        const bodyRow = tableEl.querySelector('.el-table__body-wrapper tbody tr')
        const headerH = headerRow?.getBoundingClientRect().height || 40
        let rowH = bodyRow?.getBoundingClientRect().height || 44
        if (!Number.isFinite(rowH) || rowH <= 0) rowH = 44
        repairTableHeight.value = Math.round(headerH + rowH * 10)
      })
    }

    const getStatusText = (status) => ({
      PENDING: '未处理',
      IN_PROGRESS: '处理中',
      PROCESSING: '处理中',
      PENDING_VERIFY: '处理中',
      COMPLETED: '已结单',
      CLOSED: '已结单',
      CANCELLED: '已结单'
    }[isUnifiedWorkOrders.value ? status : normalizeRepairStatus(status)] || status || '未知')

    const getStatusType = (status) => ({
      PENDING: 'warning',
      IN_PROGRESS: 'primary',
      PROCESSING: 'primary',
      PENDING_VERIFY: 'primary',
      COMPLETED: 'success',
      CLOSED: 'success',
      CANCELLED: 'success'
    }[isUnifiedWorkOrders.value ? status : normalizeRepairStatus(status)] || 'info')

    const getSourceText = (source) => ({
      AFTER_SALES: '售后台账',
      NEW_INSTALL: '新安装',
      PRODUCTION: '生产工单'
    }[source] || source || '—')

    const getActionText = (action) => ({
      CREATE: '创建工单',
      ASSIGN: '分配处理人',
      STATUS_CHANGE: '状态变更',
      FOLLOW_UP: '处理记录',
      NOTE: '新增备注',
      ADMIN_UPDATE: '管理员更新',
      VERIFY: '处理确认',
      CLOSE: '关闭工单'
    }[action] || action || '操作')

    const getLogActionTagType = (action) => ({
      CREATE: 'success',
      ASSIGN: 'warning',
      STATUS_CHANGE: 'primary',
      NOTE: 'info',
      ADMIN_UPDATE: 'primary',
      VERIFY: 'success',
      CLOSE: 'danger'
    }[action] || 'info')

    const formatDate = (value) => {
      if (!value) return '—'
      try {
        return new Intl.DateTimeFormat('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        }).format(new Date(value))
      } catch {
        return String(value)
      }
    }

    const formatBool = (value) => {
      if (value === true) return '是'
      if (value === false) return '否'
      return '—'
    }

    const formatPersonDisplay = (person, emptyText = '未指派') => {
      if (!person) return emptyText
      const username = String(person.username || '').trim()
      const displayName = String(person.displayName || '').trim()
      if (username && displayName && username !== displayName) {
        return `${username} ${displayName}`
      }
      return username || displayName || emptyText
    }

    const userDisplayMap = computed(() => {
      const map = new Map()
      const register = (person) => {
        if (!person) return
        const username = String(person.username || '').trim()
        const label = formatPersonDisplay(person, '')
        if (username && label) {
          map.set(username, label)
        }
      }
      managedUsers.value.forEach(register)
      register(userStore.userInfo)
      register(detailTarget.value?.createdBy)
      register(detailTarget.value?.processedBy)
      register(detailTarget.value?.assignee)
      register(detailTarget.value?.closedBy)
      return map
    })

    const getUserDisplayByUsername = (username, emptyText = '') => {
      const raw = String(username || '').trim()
      if (!raw) return emptyText
      return userDisplayMap.value.get(raw) || raw
    }

    const formatLogActionDetail = (detail) => {
      const text = String(detail || '').trim()
      if (!text) return '—'
      const transferMatch = text.match(/^处理人由\s+(.+?)\s+转交给\s+(.+)$/)
      if (transferMatch) {
        const fromName = transferMatch[1] === '未指派' ? '未指派' : getUserDisplayByUsername(transferMatch[1], transferMatch[1])
        const toName = transferMatch[2] === '未指派' ? '未指派' : getUserDisplayByUsername(transferMatch[2], transferMatch[2])
        return `处理人由 ${fromName} 转交给 ${toName}`
      }
      const assignMatch = text.match(/^工单已分配给处理人:\s*(.+)$/)
      if (assignMatch) {
        const assigneeName = assignMatch[1] === '未指派' ? '未指派' : getUserDisplayByUsername(assignMatch[1], assignMatch[1])
        return `工单已分配给处理人: ${assigneeName}`
      }
      return text
    }

    const formatOperatorDisplay = (operatorName) => {
      const raw = String(operatorName || '').trim()
      if (!raw) return '系统'
      return getUserDisplayByUsername(raw, raw)
    }

    const formatDuration = (ms) => {
      const totalMinutes = Math.max(0, Math.floor(ms / 60000))
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60
      if (hours <= 0) return `${minutes} 分钟`
      return `${hours} 小时 ${minutes} 分钟`
    }

    const getSlaMeta = (row) => {
      const now = nowTick.value
      if (!row) return { type: 'info', label: '暂无', subtext: '—', detail: '—' }
      const text = [
        row?.handlingMethod,
        row?.processResult,
        row?.serviceContent,
        row?.summary
      ].filter(Boolean).join(' ')
      const isShipmentTicket = /邮寄|补发|快递|物流|寄送|发配件|寄配件/i.test(text)
      const reminderHours = isShipmentTicket ? 24 : 6
      const dangerHours = reminderHours * 2
      const reminderMs = reminderHours * 3600000
      const dangerMs = dangerHours * 3600000
      const status = row.status
      if (status === 'PENDING') {
        const base = row.reportTime || row.createdAt
        if (!base) return { type: 'info', label: '未处理', subtext: '缺少报单时间', detail: '缺少报单时间' }
        const elapsed = now - new Date(base).getTime()
        if (elapsed >= dangerMs) {
          return {
            type: 'danger',
            label: `已超过${dangerHours}小时未处理`,
            subtext: `已过去 ${formatDuration(elapsed)}`,
            detail: `工单自报单后已过去 ${formatDuration(elapsed)}，已经超过 ${dangerHours} 小时未处理`
          }
        }
        if (elapsed >= reminderMs) {
          return {
            type: 'warning',
            label: `已超过${reminderHours}小时未处理`,
            subtext: `已过去 ${formatDuration(elapsed)}`,
            detail: `工单自报单后已过去 ${formatDuration(elapsed)}，已经超过 ${reminderHours} 小时未处理`
          }
        }
        const remain = reminderMs - elapsed
        return {
          type: 'success',
          label: `已过去 ${formatDuration(elapsed)}`,
          subtext: `距${reminderHours}小时提醒还剩 ${formatDuration(remain)}`,
          detail: `工单自报单后已过去 ${formatDuration(elapsed)}，距离 ${reminderHours} 小时提醒还剩 ${formatDuration(remain)}`
        }
      }
      if (status === 'IN_PROGRESS' || status === 'PROCESSING') {
        const base = row.updatedAt || row.reportTime || row.createdAt
        if (!base) return { type: 'info', label: '处理中', subtext: '缺少更新时间', detail: '缺少更新时间' }
        const elapsed = now - new Date(base).getTime()
        if (elapsed >= dangerMs) {
          return {
            type: 'danger',
            label: `已超过${dangerHours}小时未变更`,
            subtext: `已过去 ${formatDuration(elapsed)}`,
            detail: `工单自上次处理变更后已过去 ${formatDuration(elapsed)}，已经超过 ${dangerHours} 小时未变更`
          }
        }
        if (elapsed >= reminderMs) {
          return {
            type: 'warning',
            label: `已超过${reminderHours}小时未变更`,
            subtext: `已过去 ${formatDuration(elapsed)}`,
            detail: `工单自上次处理变更后已过去 ${formatDuration(elapsed)}，已经超过 ${reminderHours} 小时未变更`
          }
        }
        const remain = reminderMs - elapsed
        return {
          type: 'primary',
          label: `已过去 ${formatDuration(elapsed)}`,
          subtext: `距${reminderHours}小时提醒还剩 ${formatDuration(remain)}`,
          detail: `工单自上次处理变更后已过去 ${formatDuration(elapsed)}，距离 ${reminderHours} 小时提醒还剩 ${formatDuration(remain)}`
        }
      }
      if (status === 'PENDING_VERIFY') {
        return { type: 'primary', label: '处理中', subtext: '等待确认结果', detail: '工单已处理完成，等待验证确认' }
      }
      if (status === 'COMPLETED') {
        return { type: 'success', label: '已结单', subtext: '工单处理完成', detail: '工单已处理完成并结单' }
      }
      if (status === 'CLOSED') {
        return { type: 'info', label: '已结单', subtext: '工单已闭环', detail: '工单已结单闭环' }
      }
      if (status === 'CANCELLED') {
        return { type: 'success', label: '已结单', subtext: '工单已结束', detail: '工单已取消并按已结单处理' }
      }
      return { type: 'info', label: '待确认', subtext: '—', detail: '—' }
    }

    const getSlaType = (row) => getSlaMeta(row).type
    const getSlaLabel = (row) => getSlaMeta(row).label
    const getSlaSubtext = (row) => getSlaMeta(row).subtext
    const getSlaDetailText = (row) => getSlaMeta(row).detail

    const showError = (message) => {
      if (window.ElMessage) window.ElMessage.error(message)
      else console.error(message)
    }

    const loadManagedUsers = async () => {
      try {
        const list = await AdminTechniciansAPI.list('')
        managedUsers.value = Array.isArray(list) ? list : []
      } catch {
        managedUsers.value = []
      }
    }

    const clearExternalLocation = async () => {
      externalLocationActive.value = false
      reportStartDateFilter.value = ''
      reportEndDateFilter.value = ''
      reportDateRange.value = []
      externalLocationLabel.value = ''
      pendingOpenTicketId.value = null
      await fetchTickets()
    }

    const applyExternalFilters = async (payload) => {
      if (!payload || !payload.requestId || payload.requestId === lastExternalRequestId.value) {
        return
      }
      lastExternalRequestId.value = payload.requestId
      externalLocationActive.value = true
      sourceType.value = props.fixedSourceType || payload.sourceType || sourceType.value || 'AFTER_SALES'
      reportStartDateFilter.value = payload.reportStartDate || ''
      reportEndDateFilter.value = payload.reportEndDate || ''
      reportDateRange.value = reportStartDateFilter.value || reportEndDateFilter.value
        ? [reportStartDateFilter.value || '', reportEndDateFilter.value || '']
        : []
      externalLocationLabel.value = payload.locationLabel || ''
      pendingOpenTicketId.value = payload.openTicketId || null
      await fetchTickets()
    }

    onMounted(async () => {
      countdownTimer.value = window.setInterval(() => {
        nowTick.value = Date.now()
      }, 60000)
      await loadManagedUsers()
      sourceType.value = props.fixedSourceType || ''
      await fetchTickets()
    })
    onBeforeUnmount(() => {
      if (countdownTimer.value) {
        window.clearInterval(countdownTimer.value)
        countdownTimer.value = null
      }
    })
    watch(reportDateRange, (value) => {
      if (Array.isArray(value) && value.length === 2) {
        reportStartDateFilter.value = value[0] || ''
        reportEndDateFilter.value = value[1] || ''
      } else {
        reportStartDateFilter.value = ''
        reportEndDateFilter.value = ''
      }
    })
    watch([sourceType, statusFilter, reportDateRange], fetchTickets)
    watch(filteredTickets, computeRepairTableHeight)
    watch(() => props.externalFilters, applyExternalFilters, { deep: true })

    return {
      FEATURE_PRODUCTION_ORDERS,
      userStore,
      tickets,
      logs,
      partReplacements,
      parts,
      loading,
      exporting,
      importingExcel,
      detailVisible,
      detailTarget,
      partDialogVisible,
      apiMode,
      savingPart,
      searchKeyword,
      searchField,
      searchFieldOptions,
      searchPlaceholder,
      statusOptions,
      sourceType,
      statusFilter,
      reportDateRange,
      hasExternalLocation,
      locationSummary,
      filteredTickets,
      fetchTickets,
      exportExcel,
      downloadImportTemplate,
      beforeImportExcel,
      clearExternalLocation,
      openDetail,
      openPartDialog,
      submitPartReplacement,
      removeTicket,
      getStatusText,
      getStatusType,
      getSourceText,
      getActionText,
      getLogActionTagType,
      getSlaType,
      getSlaLabel,
      getSlaSubtext,
      getSlaDetailText,
      formatDate,
      normalizeRepairStatus,
      formatBool,
      formatPersonDisplay,
      formatLogActionDetail,
      formatOperatorDisplay,
      repairTableRef,
      repairTableHeight,
      partForm
    }
  }
}
</script>

<style scoped>
.repair-list {
  padding: 8px;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 12px;
}

.location-alert {
  margin-bottom: 12px;
}

.toolbar-input {
  width: 320px;
}

.toolbar-select {
  width: 140px;
}

.toolbar-date-range {
  width: 300px;
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.sla-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sla-subtext {
  color: #64748b;
  font-size: 12px;
  line-height: 1.4;
}

.detail-block {
  margin-bottom: 20px;
}

.detail-summary-card {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px;
  border-radius: 14px;
  background: linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%);
  border: 1px solid #dbeafe;
}

.detail-summary-main {
  flex: 1;
  min-width: 0;
}

.detail-summary-title {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
}

.detail-summary-subtitle {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  color: #64748b;
  font-size: 13px;
}

.detail-summary-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(120px, 1fr));
  gap: 12px;
  min-width: 360px;
}

.summary-stat {
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: inset 0 0 0 1px rgba(219, 234, 254, 0.9);
}

.summary-stat-label {
  display: block;
  margin-bottom: 6px;
  color: #94a3b8;
  font-size: 12px;
}

.summary-stat-value {
  display: block;
  color: #1e293b;
  font-size: 14px;
  font-weight: 600;
  word-break: break-all;
}

.detail-info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.info-card {
  padding: 18px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #edf2f7;
}

.detail-title {
  margin-bottom: 12px;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.log-bubble-track {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.log-bubble {
  min-width: 220px;
  max-width: 280px;
  padding: 12px 14px;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
}

.log-bubble-time {
  color: #64748b;
  font-size: 12px;
  margin-bottom: 8px;
}

.log-bubble-title {
  margin-bottom: 8px;
}

.log-bubble-desc {
  color: #334155;
  line-height: 1.7;
  word-break: break-word;
}

.log-bubble-meta {
  margin-top: 8px;
  color: #94a3b8;
  font-size: 12px;
}

.log-bubble-arrow {
  color: #94a3b8;
  font-size: 28px;
  line-height: 1;
  font-weight: 500;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.log-title {
  font-weight: 600;
  color: #303133;
}

.log-desc {
  margin-top: 4px;
  color: #606266;
}

.log-meta {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
}

@media (max-width: 1200px) {
  .detail-summary-card,
  .detail-info-grid {
    grid-template-columns: 1fr;
    display: grid;
  }

  .detail-summary-stats {
    min-width: 0;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .detail-info-grid,
  .detail-summary-stats {
    grid-template-columns: 1fr;
  }

  .log-bubble-arrow {
    display: none;
  }
}
</style>
